import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';

import ReferralContext from './ReferralContext';
import useUser from '../UserContext/useUser';
import { fetchState } from '../UserContext';
import {
  buildReferralInfo,
  getAffiliateInfo,
  parseReferralCookie,
  setAffiliateCookie,
  setReferralCookie,
} from './referralUtils';
import { fetchMyReferrerProfile, fetchPublicReferralByCode } from '../../api/referrals';
import { getCookie, removeCookie } from '../../util/cookies';

const isLoggedInUser = (userFetchState, user) =>
  userFetchState === fetchState.COMPLETE && Object.keys(user).length > 0;

const getIncomingReferralCode = () => {
  if (typeof window === 'undefined') return null;

  return new URLSearchParams(window.location.search).get('referralCode')?.trim() || null;
};

export default function ReferralProvider({ children }) {
  const router = useRouter();
  const { user, userFetchState } = useUser();
  const [referralInfo, setReferralInfo] = useState({});
  const [affiliateInfo, setAffiliateInfo] = useState();
  const processedIncomingCodeRef = useRef(null);

  useEffect(() => {
    const nextAffiliateInfo = getAffiliateInfo();
    if (nextAffiliateInfo?.code && !nextAffiliateInfo?.hasCookie) {
      setAffiliateCookie(nextAffiliateInfo);
    }

    setAffiliateInfo(nextAffiliateInfo);
  }, [router.asPath]);

  useEffect(() => {
    if (getIncomingReferralCode()) return;

    processedIncomingCodeRef.current = null;

    const existingReferral = getCookie('referral');
    if (existingReferral?.includes('code|')) {
      setReferralInfo(parseReferralCookie(existingReferral));
      return;
    }

    setReferralInfo({});
  }, [router.asPath]);

  useEffect(() => {
    if (userFetchState === fetchState.PENDING) return undefined;

    const incomingCode = getIncomingReferralCode();
    if (!incomingCode) return undefined;
    if (processedIncomingCodeRef.current === incomingCode) return undefined;

    processedIncomingCodeRef.current = incomingCode;

    let cancelled = false;

    const resolveIncomingReferral = async () => {
      setReferralInfo({});

      const publicReferrer = await fetchPublicReferralByCode(incomingCode);
      if (cancelled) return;

      if (publicReferrer.status !== 200 || !publicReferrer.data?.referralCode) {
        return;
      }

      if (isLoggedInUser(userFetchState, user)) {
        const currentReferrer = await fetchMyReferrerProfile();
        if (cancelled) return;

        if (
          currentReferrer.status === 200 &&
          currentReferrer.data?.referralCode === publicReferrer.data.referralCode
        ) {
          removeCookie('referral');
          setReferralInfo({});
          router.replace('/?utm=invalidreferral');
          return;
        }
      }

      setReferralCookie(publicReferrer.data.name, publicReferrer.data.referralCode);
      setReferralInfo(buildReferralInfo(publicReferrer.data));
    };

    resolveIncomingReferral().catch((error) => {
      console.error('Failed to resolve referral code', error);
      setReferralInfo({});
    });

    return () => {
      cancelled = true;
    };
  }, [router, router.asPath, user, userFetchState]);

  return (
    <ReferralContext.Provider
      value={useMemo(() => ({
        referralInfo,
        affiliateInfo,
      }), [affiliateInfo, referralInfo])}>
      {children}
    </ReferralContext.Provider>
  );
}

ReferralProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
